import { supabase, addActivityLog } from './supabase';
import {
  addAxisCareClient, updateAxisCareClient, getAxisCareClients, parseAxisCareClients,
  normalizeClientName, createNote,
} from './axiscare';

// Creating a CAERA client in AxisCare — one path for both the automatic push
// when a client moves to Active (clientStatus.js) and the "Create in AxisCare"
// button on the profile, so the two cannot drift apart (same reasoning as
// axiscarePush.js for caregivers).
//
// AxisCare's client schema is only partly pinned down. The CORE payload below
// is the set confirmed against their API when the button was built, and the
// record is created with that alone, so a field AxisCare does not recognise
// can never stop the client being created. Everything else is sent afterwards,
// one PATCH per field group: a group AxisCare rejects is listed in `skipped`
// and the rest still land. What has no AxisCare field at all (guardian,
// emergency contact, physician, diagnoses, allergies, the PA) goes on as one
// AxisCare note, so the scheduler sees it without opening CAERA.
//
// Languages and allergies are deliberately not sent as fields: when the button
// was built they were accepted but did not persist. They are in the note.

const clean = (v) => (v == null ? '' : String(v).trim());
const orUndef = (v) => clean(v) || undefined;

// The error AxisCare actually returned, not supabase-js's generic
// "Edge Function returned a non-2xx status code": the proxy passes AxisCare's
// own body through, and that body names the field it objected to.
export const axisErrorText = async (error) => {
  if (!error) return '';
  try {
    const body = await error.context?.json?.();
    const msg = body?.errors?.[0]?.message || body?.errors?.[0] || body?.message || body?.error;
    if (msg) return typeof msg === 'string' ? msg : JSON.stringify(msg);
  } catch { /* body unreadable — fall through */ }
  return error.message || 'AxisCare request failed';
};

const address = (c) => (clean(c.address) ? {
  streetAddress1: clean(c.address),
  city: clean(c.city),
  state: clean(c.state),
  postalCode: clean(c.zip),
} : null);

// AxisCare wants M/F, not the word — "Male"/"Female" is rejected outright.
const axisGender = (g) => (clean(g) ? clean(g).charAt(0).toUpperCase() : undefined);

// Most CAERA clients are children: when the record has no phone or email of
// the child's own, the family's is the one the office and caregivers need.
export const corePayload = (c) => {
  const addr = address(c);
  return {
    firstName: clean(c.first_name),
    lastName: clean(c.last_name),
    goesBy: orUndef(c.goes_by),
    dateOfBirth: orUndef(c.date_of_birth),
    personalEmail: orUndef(c.email) || orUndef(c.guardian_email),
    mobilePhone: orUndef(c.phone) || orUndef(c.guardian_phone),
    medicaidNumber: orUndef(c.medicaid_id),
    gender: axisGender(c.gender),
    status: 'Active',
    ...(addr ? { residentialAddress: addr } : {}),
  };
};

// Field groups sent one at a time after the create. Each is [label, body];
// a group with nothing to send is left out.
export const extraGroups = (c) => {
  const addr = address(c);
  const groups = [
    ['middle name', c.middle_name && { middleName: clean(c.middle_name) }],
    ['suffix', c.suffix && { suffix: clean(c.suffix) }],
    ['family phone', c.phone && c.guardian_phone && { homePhone: clean(c.guardian_phone) }],
    ['alternate phone', c.guardian_alt_phone && { otherPhone: clean(c.guardian_alt_phone) }],
    ['mailing address', addr && { mailingAddress: addr }],
    ['billing address', addr && { billingAddress: addr }],
    ['map location', addr && c.latitude != null && c.longitude != null && {
      residentialAddress: { ...addr, latitude: Number(c.latitude), longitude: Number(c.longitude) },
    }],
  ];
  return groups.filter(([, body]) => body);
};

const fullName = (first, last) => [clean(first), clean(last)].filter(Boolean).join(' ');
const line = (label, ...parts) => {
  const v = parts.map(clean).filter(Boolean).join(' · ');
  return v ? `${label}: ${v}` : null;
};

// The CAERA record AxisCare has no field for, as one plain-text note.
export const noteText = (c, pa) => [
  `Set up from CAERA (${clean(c.client_id)}) on ${new Date().toLocaleDateString('en-US')}.`,
  line('Payer', c.service_type === 'gapp' ? 'GAPP' : c.service_type),
  line('Guardian', fullName(c.guardian_first_name, c.guardian_last_name), c.guardian_relationship, c.guardian_phone, c.guardian_alt_phone, c.guardian_email),
  line('Emergency contact', fullName(c.emergency_first_name, c.emergency_last_name), c.emergency_relationship, c.emergency_phone, c.emergency_alt_phone),
  line('Physician', c.doctor_name, c.doctor_practice, c.doctor_phone, c.doctor_fax && `fax ${c.doctor_fax}`, c.doctor_npi && `NPI ${c.doctor_npi}`),
  line('Diagnosis', c.diagnosis_code, c.diagnosis_summary),
  line('Allergies', c.has_allergies === false ? 'None known' : c.allergies),
  line('Language', c.language),
  line('Equipment', (c.medical_equipment || []).join(', ')),
  line('Seizures', c.seizures ? 'Yes' : null),
  line('G-tube', c.g_tube ? 'Yes' : null),
  line('Oxygen', c.uses_oxygen ? 'Yes' : null),
  pa && line('Prior auth', pa.auth_number && `#${pa.auth_number}`,
    (pa.effective_date || pa.submitted_date || pa.expiry_date) && `${pa.effective_date || pa.submitted_date || '?'} to ${pa.expiry_date || '?'}`,
    pa.skilled_hours_week != null && `skilled ${Number(pa.skilled_hours_week)} h/wk`,
    pa.pss_hours_week != null && `PSS ${Number(pa.pss_hours_week)} h/wk`),
  line('Pets in home', (c.household_pets || []).join(', '), c.household_pets_other),
].filter(Boolean).join('\n');

/**
 * Looks for this client already in AxisCare. AxisCare refuses a second client
 * with the same first and last name, so this has to run before a create.
 * `sameDob` is true only when both records carry a date of birth and it agrees
 * — the one case where linking without asking a person is safe.
 */
export const findAxisCareMatch = async (client) => {
  const { data, error } = await getAxisCareClients();
  if (error) throw new Error(`Could not reach AxisCare to check for an existing record: ${await axisErrorText(error)}`);
  const target = normalizeClientName(client.first_name, client.last_name);
  const match = parseAxisCareClients(data).find(c => normalizeClientName(c.firstName, c.lastName) === target);
  if (!match) return null;
  const theirDob = clean(match.dateOfBirth).slice(0, 10);
  return { match, sameDob: !!theirDob && theirDob === clean(client.date_of_birth).slice(0, 10) };
};

const linkClient = async (clientId, axisId) => {
  const { error } = await supabase.from('clients').update({ axiscare_client_id: axisId }).eq('id', clientId);
  if (error) throw new Error(`Failed to save the AxisCare link: ${error.message}`);
};

const logIt = (client, profile, notes) => addActivityLog({
  client_id: client.id, client_ref: client.client_id, activity_type: 'AxisCare Setup', notes,
  performed_by_name: profile?.full_name || 'CAERA', performed_by_id: profile?.id || null,
});

/**
 * Create `client` in AxisCare, or link the record that is already there.
 *
 * `confirmLink(match)` decides a same-name record whose date of birth does not
 * confirm it is the same child: the button asks the person; the automatic
 * path passes nothing and is told to leave it for a person.
 *
 * Returns { outcome, axisId, sent, skipped }, where outcome is 'created',
 * 'linked', 'already_linked' (the saved row was linked after the caller
 * loaded it), 'needs_review' (same name, unconfirmed — nothing done) or
 * 'declined'. Throws when AxisCare refuses the create itself; the CAERA
 * record is then left unlinked so the button can try again.
 */
export async function pushClientToAxisCare(clientIn, { profile, confirmLink } = {}) {
  // Always work from the saved row — callers hold whatever subset of columns
  // their screen loaded.
  const { data: client, error: loadErr } = await supabase.from('clients').select('*').eq('id', clientIn.id).single();
  if (loadErr) throw new Error(`Could not load the client: ${loadErr.message}`);
  if (client.axiscare_client_id) return { outcome: 'already_linked', axisId: client.axiscare_client_id, sent: [], skipped: [] };

  const found = await findAxisCareMatch(client);
  if (found) {
    const { match, sameDob } = found;
    const ok = sameDob || (confirmLink ? await confirmLink(match) : false);
    if (!ok) return { outcome: confirmLink ? 'declined' : 'needs_review', axisId: match.id, sent: [], skipped: [] };
    await linkClient(client.id, match.id);
    // The record found may have been left Inactive; the client is Active now.
    await updateAxisCareClient(match.id, { status: 'Active' });
    await logIt(client, profile, `Linked to the existing AxisCare client, ID ${match.id}${sameDob ? ' (same name and date of birth)' : ''}.`);
    return { outcome: 'linked', axisId: match.id, sent: [], skipped: [] };
  }

  const core = corePayload(client);
  const { data, error } = await addAxisCareClient(core);
  if (error) throw new Error(await axisErrorText(error));
  const axisId = data?.id || data?.results?.id || data?.results?.data?.id || data?.client?.id;
  if (!axisId) throw new Error('AxisCare responded but no client ID was found in the response — the payload field names may need adjusting.');
  await linkClient(client.id, axisId);

  const sent = [
    ['name', core.firstName], ['date of birth', core.dateOfBirth], ['gender', core.gender],
    ['Medicaid #', core.medicaidNumber], ['phone', core.mobilePhone], ['email', core.personalEmail],
    ['home address', core.residentialAddress],
  ].filter(([, v]) => v).map(([label]) => label);
  const skipped = [];

  for (const [label, body] of extraGroups(client)) {
    const { data: res, error: err } = await updateAxisCareClient(axisId, body);
    if (err || res?.errors?.length) skipped.push(label); else sent.push(label);
  }

  const { data: pa } = await supabase.from('prior_auth_history')
    .select('auth_number, submitted_date, effective_date, expiry_date, skilled_hours_week, pss_hours_week')
    .eq('client_id', client.id).eq('auth_type', 'PA').eq('status', 'active')
    .order('expiry_date', { ascending: false, nullsFirst: false }).limit(1).maybeSingle();
  const { data: noteRes, error: noteErr } = await createNote({ entityType: 'client', entityId: axisId, note: noteText(client, pa) });
  if (noteErr || noteRes?.errors?.length) skipped.push('care summary note'); else sent.push('care summary note');

  await logIt(client, profile, [
    `Created in AxisCare, ID ${axisId}.`,
    `Sent: ${sent.join(', ')}.`,
    skipped.length ? `AxisCare did not accept: ${skipped.join(', ')} — add these in AxisCare by hand.` : null,
  ].filter(Boolean).join(' '));

  return { outcome: 'created', axisId, sent, skipped };
}
